import { LoadingSpinner } from '@ais-ui/ui-core';
import {
  ImportStatusProvider,
  useUserSettingsImport,
} from '@ais-ui/user-settings';
import {
  Settings,
  useGetSettings,
  useSetMapLegendItems,
} from '@ais-ui/websockets';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useSettingsChecker } from './settingsChecker';
import { useReplayContext } from '../Replay/ReplayContext';
import { SharedSettingsProvider } from './SharedSettingsContext';
import { MapViewportProvider } from './MapViewportContext';

interface SettingsProviderProps {
  children: ReactNode;
}

interface SettingsContextType {
  settings: Settings;
  dataUpdatedAt: number | null;
}

const SettingsContext = createContext<SettingsContextType | null>(null);

/**
 * Runs the login-time user-settings import once per session. Rendered inside
 * `ImportStatusProvider` (which it reads) and only after the blob is ready, so
 * the orchestrator has everything it needs to seed the service.
 */
const UserSettingsImportRunner = () => {
  useUserSettingsImport();
  return null;
};

export const SettingsProvider = ({ children }: SettingsProviderProps) => {
  const { isPlaying } = useReplayContext();
  const [settings, setSettings] = useState<Settings | null>(null);
  const { hasCheckedSettings } = useSettingsChecker(settings);
  const { data: fetchedSettings, status, dataUpdatedAt } = useGetSettings();

  const [hasUpdatedLegend, setHasUpdatedLegend] = useState(false);

  const { mutate } = useSetMapLegendItems(isPlaying);

  useEffect(() => {
    if (status === 'success' && fetchedSettings) {
      setSettings(fetchedSettings);
    }
  }, [status, fetchedSettings, dataUpdatedAt]);

  // This is a bit of hack. The backend should be able to tell what items are in no show, but
  //  at the moment it doesn't. So we need to tell it what items are in no show.
  useEffect(() => {
    if (settings && !hasUpdatedLegend) {
      mutate({
        mapLegendItems:
          settings['ribbonPlugin.settings']?.ship_color_noshow_sel || [],
        showItem: false,
      });
      setHasUpdatedLegend(true);
    }
  }, [settings, hasUpdatedLegend, mutate]);

  if (!settings || !hasCheckedSettings) {
    return <LoadingSpinner className="h-screen w-screen" />;
  }

  return (
    <SettingsContext.Provider
      value={{
        settings,
        dataUpdatedAt,
      }}
    >
      <MapViewportProvider>
        <SharedSettingsProvider
          settings={settings}
          dataUpdatedAt={dataUpdatedAt}
        >
          <ImportStatusProvider>
            <UserSettingsImportRunner />
            {children}
          </ImportStatusProvider>
        </SharedSettingsProvider>
      </MapViewportProvider>
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(
      'useSettingsContext must be used within a SettingsContextProvider',
    );
  }
  return context;
};
